import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { TrendingUp, CheckCircle, Clock, BookOpen, PlayCircle, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress as ProgressBar } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";

export default function Progress() {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  
  const { data: progressData, refetch: refetchProgress } = useQuery({
    queryKey: ['/progress'],
    staleTime: 0, // Always consider data stale
    gcTime: 0, // Don't cache
  }) as { data: { progress: any[] } | undefined; refetch: () => void };

  // Debug progress data changes
  useEffect(() => {
    console.log("Progress page - Progress data updated:", progressData);
  }, [progressData]);

  const { data: modulesData } = useQuery({
    queryKey: ['/training-modules'],
  }) as { data: { modules: any[] } | undefined };

  // Progress update mutation
  const updateProgressMutation = useMutation({
    mutationFn: async (data: { moduleId: string; isCompleted: boolean; timeSpent?: number }) => {
      console.log("Progress page - Updating progress:", data);
      const response = await apiRequest("POST", "/progress", data);
      const result = await response.json();
      console.log("Progress page - Update response:", result);
      return result;
    },
    onSuccess: (data) => {
      console.log("Progress page - Update successful:", data);
      console.log("Progress page - Invalidating queries...");
      queryClient.invalidateQueries({ queryKey: ['/progress'] });
      queryClient.invalidateQueries({ queryKey: ['/progress/overall'] });
      
      // Force refetch the progress data
      queryClient.refetchQueries({ queryKey: ['/progress'] });
      
      // Also manually refetch
      setTimeout(() => {
        console.log("Progress page - Manually refetching progress...");
        refetchProgress();
      }, 100);
    },
    onError: (error: Error) => {
      console.error("Progress page - Update error:", error);
      toast({
        title: "Error",
        description: "Failed to update progress",
        variant: "destructive",
      });
    },
  });

  // Handle module click to toggle completion
  const handleModuleClick = (moduleId: string) => {
    console.log("Module clicked:", moduleId);
    
    // Find current progress for this module
    const currentProgress = progressData?.progress?.find((p: any) => p.moduleId === moduleId);
    const isCurrentlyCompleted = currentProgress?.isCompleted || false;
    
    // Toggle completion status
    const newCompletionStatus = !isCurrentlyCompleted;
    
    console.log("Toggling module completion:", moduleId, "from", isCurrentlyCompleted, "to", newCompletionStatus);
    
    // Update progress in database
    updateProgressMutation.mutate({
      moduleId: moduleId,
      isCompleted: newCompletionStatus,
      timeSpent: newCompletionStatus ? 300 : 0, // 5 minutes if completing, 0 if uncompleting
    });
    
    // Show toast notification
    if (newCompletionStatus) {
      toast({
        title: "Module Completed!",
        description: "Module has been marked as completed.",
      });
    } else {
      toast({
        title: "Module Reset",
        description: "Module has been marked as incomplete.",
      });
    }
  };

  // Create training modules data from database
  const createTrainingModules = () => {
    if (!modulesData?.modules) return [];
    
    console.log("Progress page - Creating training modules with data:", {
      modules: modulesData.modules,
      progress: progressData?.progress
    });
    
    return modulesData.modules.map((module: any) => {
      const progress = progressData?.progress?.find((p: any) => p.moduleId === module.id);
      const isCompleted = progress?.isCompleted || false;
      
      console.log(`Progress page - Module ${module.title} (${module.id}):`, {
        progress: progress,
        isCompleted: isCompleted
      });
      
      return {
        ...module,
        id: module.id,
        isCompleted: isCompleted,
        completedAt: isCompleted ? new Date().toISOString() : null
      };
    });
  };

  const allModules = createTrainingModules();
  const completedModules = allModules.filter(module => module.isCompleted).length;
  const totalModules = allModules.length;
  const completionPercentage = totalModules > 0 ? Math.round((completedModules / totalModules) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="progress">
      <div>
        <h1 className="text-3xl font-bold text-servify-dark-grey">My Progress</h1>
        <p className="text-servify-medium-grey">Track your learning journey and achievements</p>
        {/* Debug info - remove this later */}

      </div>

      {/* Progress Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-servify-light-grey shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-servify-purple/10 rounded-lg flex items-center justify-center">
                <CheckCircle className="text-servify-purple h-6 w-6" />
              </div>
              <span className="text-2xl font-bold text-servify-purple">{completedModules}</span>
            </div>
            <h3 className="font-semibold text-servify-dark-grey mb-1">Completed Modules</h3>
            <p className="text-servify-medium-grey text-sm">Training modules finished</p>
          </CardContent>
        </Card>

        <Card className="border-servify-light-grey shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-servify-purple/10 rounded-lg flex items-center justify-center">
                <BookOpen className="text-servify-purple h-6 w-6" />
              </div>
              <span className="text-2xl font-bold text-servify-purple">{totalModules}</span>
            </div>
            <h3 className="font-semibold text-servify-dark-grey mb-1">Total Modules</h3>
            <p className="text-servify-medium-grey text-sm">Available for completion</p>
          </CardContent>
        </Card>

        <Card className="border-servify-light-grey shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-servify-purple/10 rounded-lg flex items-center justify-center">
                <TrendingUp className="text-servify-purple h-6 w-6" />
              </div>
              <span className="text-2xl font-bold text-servify-purple">{completionPercentage}%</span>
            </div>
            <h3 className="font-semibold text-servify-dark-grey mb-1">Overall Progress</h3>
            <p className="text-servify-medium-grey text-sm">Completion percentage</p>
          </CardContent>
        </Card>
      </div>

      {/* Training Modules List */}
      <Card className="border-gray-300 shadow-lg">
        <CardHeader>
          <CardTitle>
            <span>Module-wise Progress</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {allModules.map((module) => (
              <div 
                key={`${module.id}-${module.isCompleted}`}
                className={`space-y-2 p-3 rounded-lg ${
                  module.isCompleted ? 'bg-white border-2 border-green-200' : 'bg-white border border-gray-200'
                }`}
                data-testid={`module-progress-${module.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      module.isCompleted ? 'bg-green-100' : 'bg-gray-100'
                    }`}>
                      {module.isCompleted ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <PlayCircle className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium">{module.title}</h4>
                        {module.isCompleted && (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                            Completed
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{module.description}</p>
                      <p className="text-xs text-gray-500">Day {module.day} • {module.duration} minutes</p>
                      {module.isCompleted && module.completedAt && (
                        <p className="text-xs text-green-600">
                          Completed on {new Date(module.completedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="font-semibold">{module.isCompleted ? 100 : 0}%</span>
                    <p className="text-sm text-gray-600">
                      {module.isCompleted ? 'Completed' : 'Not Started'}
                    </p>
                  </div>
                </div>
                <ProgressBar value={module.isCompleted ? 100 : 0} className="h-2" />
              </div>
            ))}
            
            {allModules.length === 0 && (
              <p className="text-center text-gray-500 py-8">No training modules available yet.</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}